import type { FluxErrorEntityErrorCodes } from '../../types.js';

export const AutomationV2ExecutionActionMakeHttpRequestCodes = {
  AutomationV2ExecutionActionMakeHttpRequestExpectedStatusCodeMismatch: {
    code: 'automation-v2-execution-action/make-http-request-expected-status-code-mismatch',
    explanation: `A requisição foi completada, mas retornou um status HTTP diferente do configurado como sucesso.

**Causas comuns:**
- API externa retornou erro (4xx, 5xx)
- Expectativa configurada como 200 mas recebeu 201

**Como diagnosticar:**
Verifique os logs da execução para ver o status code real retornado pela API externa.`,
    localizations: {},
    message: 'O código de status retornado pela requisição HTTP não corresponde ao esperado.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlDirectIpNotAllowed: {
    code: 'automation-v2-execution-action/make-http-request-url-direct-ip-not-allowed',
    explanation: `Por segurança, requisições para IPs diretos são bloqueadas.

**Causas comuns:**
- Uso de \`http://1.2.3.4\` em vez de domínio
- URLs internas de rede

**Como resolver:**
Utilize nomes de domínio (DNS) qualificados.`,
    localizations: {},
    message: 'Não é permitido utilizar endereços IP diretamente na URL da requisição HTTP.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlInvalidOrUnresolvable: {
    code: 'automation-v2-execution-action/make-http-request-url-invalid-or-unresolvable',
    explanation: `O DNS não conseguiu resolver o domínio da URL.

**Causas comuns:**
- Domínio inexistente
- Erro de digitação na URL
- Falha temporária de DNS

**Como resolver:**
Verifique se a URL está correta e acessível publicamente.`,
    localizations: {},
    message: 'A URL informada na requisição HTTP é inválida ou não pode ser resolvida.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlPortNotAllowed: {
    code: 'automation-v2-execution-action/make-http-request-url-port-not-allowed',
    explanation: `A porta especificada na URL está bloqueada.

**Causas comuns:**
- Tentativa de usar portas não-padrão (diferentes de 80 e 443)
- Portas de serviços internos (ex: 22, 25, 3306)

**Como resolver:**
Utilize apenas portas HTTP (80) ou HTTPS (443).`,
    localizations: {},
    message: 'A porta informada na URL da requisição HTTP não é permitida.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlPrivateNetwork: {
    code: 'automation-v2-execution-action/make-http-request-url-private-network',
    explanation: `Tentativa de acesso a endereços de rede privada (localhost, 192.168.x.x, etc).

**Causas comuns:**
- URL aponta para servidor local de desenvolvimento
- DNS resolve para IP privado (DNS Rebinding protection)

**Como resolver:**
A automação só pode acessar URLs públicas na internet. Use um túnel (ex: ngrok) para testes locais.`,
    localizations: {},
    message: 'Não é permitido realizar requisições HTTP para redes privadas.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlProtocolNotAllowed: {
    code: 'automation-v2-execution-action/make-http-request-url-protocol-not-allowed',
    explanation: `Protocolo da URL não suportado.

**Causas comuns:**
- Uso de \`ftp://\`, \`file://\` ou outros protocolos
- Esquecer o protocolo (ex: começar direto com \`www\`)

**Como resolver:**
Use sempre \`http://\` ou \`https://\`.`,
    localizations: {},
    message: 'O protocolo informado na URL da requisição HTTP não é permitido.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
  AutomationV2ExecutionActionMakeHttpRequestUrlResolvedIpBlacklisted: {
    code: 'automation-v2-execution-action/make-http-request-url-resolved-ip-blacklisted',
    explanation: `O IP de destino está na lista de bloqueio de segurança.

**Causas comuns:**
- IP conhecido por malware/spam
- IPs de infraestrutura interna do provedor de cloud

**Como resolver:**
Verifique se o destino da requisição é seguro e público.`,
    localizations: {},
    message: 'O endereço IP resolvido da URL não é permitido.',
    supportingDocs: [
      {
        name: 'Ação de Fazer Requisição HTTP',
        path: '/wiki/docs/automations/actions/make-http-request',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action'>;
